import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from 'react';
import type { Tenant } from '../api/types';

const STORAGE_KEY = 'pdr_selected_tenant';

interface TenantContextValue {
  tenantId: string | null;
  tenants: Tenant[];
  setTenantId: (id: string) => void;
  setTenants: (tenants: Tenant[]) => void;
  isLoading: boolean;
}

const TenantContext = createContext<TenantContextValue | null>(null);

interface TenantProviderProps {
  children: ReactNode;
}

export function TenantProvider({ children }: TenantProviderProps) {
  const [tenantId, setTenantIdState] = useState<string | null>(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem(STORAGE_KEY);
    }
    return null;
  });
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const setTenantId = useCallback((id: string) => {
    setTenantIdState(id);
    localStorage.setItem(STORAGE_KEY, id);
  }, []);

  // Auto-select first tenant if none selected
  useEffect(() => {
    if (!tenantId && tenants.length > 0) {
      setTenantId(tenants[0].id);
    }
    if (tenants.length > 0) {
      setIsLoading(false);
    }
  }, [tenants, tenantId, setTenantId]);

  const value: TenantContextValue = {
    tenantId,
    tenants,
    setTenantId,
    setTenants,
    isLoading,
  };

  return (
    <TenantContext.Provider value={value}>
      {children}
    </TenantContext.Provider>
  );
}

export function useTenant(): TenantContextValue {
  const context = useContext(TenantContext);
  if (!context) {
    throw new Error('useTenant must be used within a TenantProvider');
  }
  return context;
}

export function useRequiredTenant(): string {
  const { tenantId, isLoading } = useTenant();
  if (isLoading) {
    throw new Error('Tenant is still loading');
  }
  if (!tenantId) {
    throw new Error('No tenant selected');
  }
  return tenantId;
}
